import { useState, useEffect, useCallback } from "react"
import { Loader2, CheckCircle2, XCircle } from "lucide-react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import * as z from "zod"
import { zodResolver } from "@hookform/resolvers/zod"

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@f2e/ui/components/base"
import {
  ClientChannel,
  ClientChannelConfig,
  ClientChannelType,
  ExtensionClientConfig,
  InstagramClientConfig,
  LineClientConfig,
  MessengerClientConfig,
  SlackClientConfig,
  WebClientConfig,
  WidgetClientConfig,
  Workspace,
} from "@/types"
import { useToast, useDebouncedEffect } from "@/hooks"
import {
  FetchStatus,
  useCheckSlugAvailabilityApi,
  useCreateClientChannelApi,
  clientChannelResponseToClientChannel,
  makeCreateClientChannelRequest,
} from "@/hooks/api"
import { getLimitErrorTranslationKey } from "@/constants/errors"
import {
  ClientChannelSlugIssue,
  getClientChannelSlugIssue,
  isReservedClientChannelSlug,
} from "@/constants/clientChannels"
import {
  getApiErrorData,
  getApiErrorPayloadMessage,
  getUserFacingApiErrorMessage,
  normalizeApiError,
} from "@/lib/apiErrors"

type SlugCheckStatus =
  | "idle"
  | "checking"
  | "available"
  | "unavailable"
  | "reserved"

const createChannelFormSchema = (t: (key: string) => string) =>
  z.object({
    name: z
      .string()
      .min(1, { message: t("workspace.channels.form.errors.nameRequired") }),
    slug: z
      .string()
      .min(1, { message: t("workspace.channels.form.errors.slugRequired") })
      .regex(/^[a-z0-9-]+$/, {
        message: t("workspace.channels.form.errors.slugInvalid"),
      }),
    type: z.nativeEnum(ClientChannelType),
  })

export type ChannelFormData = z.infer<
  ReturnType<typeof createChannelFormSchema>
>

export interface ChannelDialogProps {
  open: boolean
  workspace: Workspace
  onOpenChange?: (open: boolean) => void
  onCreated?: (channel: ClientChannel) => void
  prefillAgentId?: string
}

export const ChannelDialog = ({
  open,
  workspace,
  onOpenChange,
  onCreated,
  prefillAgentId = "",
}: ChannelDialogProps) => {
  const { t } = useTranslation()
  const { successToast, alertToast } = useToast()
  const [slugCheckStatus, setSlugCheckStatus] =
    useState<SlugCheckStatus>("idle")

  const ChannelFormSchema = createChannelFormSchema(t)
  const form = useForm<ChannelFormData>({
    resolver: zodResolver(ChannelFormSchema),
    defaultValues: {
      name: "",
      slug: "",
      type: prefillAgentId ? ClientChannelType.WIDGET : ClientChannelType.WEB,
    },
  })

  const nameValue = form.watch("name")
  const slugValue = form.watch("slug")

  useEffect(() => {
    if (!open) return
    form.setValue(
      "type",
      prefillAgentId ? ClientChannelType.WIDGET : ClientChannelType.WEB,
      { shouldDirty: false, shouldValidate: true }
    )
  }, [form, open, prefillAgentId])

  const applySlugIssue = useCallback(
    (issue: ClientChannelSlugIssue | null) => {
      if (!issue) {
        form.clearErrors("slug")
        return
      }

      form.setError("slug", {
        type: "manual",
        message:
          issue === "reserved"
            ? t("workspace.channels.form.errors.slugReserved")
            : t("workspace.channels.form.errors.slugTaken"),
      })
    },
    [form, t]
  )

  // APIs
  const {
    data: checkSlugRespData,
    status: checkSlugRespStatus,
    error: checkSlugRespError,
    fetchFn: checkSlug,
  } = useCheckSlugAvailabilityApi(workspace.organizationId || "", workspace.id)
  const {
    data: createChannelRespData,
    error: createChannelRespError,
    status: createChannelRespStatus,
    fetchFn: createChannel,
  } = useCreateClientChannelApi(workspace.organizationId || "", workspace.id)

  // Debounce slug check when user types
  useDebouncedEffect({
    callback: () => {
      checkSlug({
        searchParams: new URLSearchParams({
          slug: slugValue,
        }),
      })
    },
    delay: 800,
    condition: () => {
      if (!slugValue) return false
      const slugRegex = /^[a-z0-9-]+$/
      return (
        slugRegex.test(slugValue) && !isReservedClientChannelSlug(slugValue)
      )
    },
    onDebounceStart: () => {
      setSlugCheckStatus("checking")
      form.clearErrors("slug")
    },
    onSkip: () => {
      if (!slugValue) {
        setSlugCheckStatus("idle")
        form.clearErrors("slug")
        return
      }

      if (isReservedClientChannelSlug(slugValue)) {
        setSlugCheckStatus("reserved")
        applySlugIssue("reserved")
        return
      }

      setSlugCheckStatus("idle")
    },
    deps: [slugValue, checkSlug],
  })

  // Handle check slug response
  useEffect(() => {
    if (!checkSlugRespData || !checkSlugRespData.data) return

    const isAvailable = checkSlugRespData.data?.available || false
    setSlugCheckStatus(isAvailable ? "available" : "unavailable")

    if (!isAvailable) {
      applySlugIssue("taken")
    } else {
      applySlugIssue(null)
    }
  }, [applySlugIssue, checkSlugRespData])

  // Handle check slug error
  useEffect(() => {
    if (!checkSlugRespError) return

    const normalizedError = normalizeApiError(checkSlugRespError)
    if (normalizedError.messageKey) {
      setSlugCheckStatus("idle")
      alertToast({
        title: t("workspace.channels.messages.error.checkSlug.title"),
        description: t(normalizedError.messageKey),
      })
      return
    }

    const slugIssue = getClientChannelSlugIssue(
      slugValue,
      getApiErrorData(checkSlugRespError)?.message || checkSlugRespData?.message
    )
    if (slugIssue === "reserved") {
      setSlugCheckStatus("reserved")
      applySlugIssue(slugIssue)
      return
    }

    setSlugCheckStatus("idle")
    alertToast({
      title: t("workspace.channels.messages.error.checkSlug.title"),
      description: getApiErrorPayloadMessage(
        getApiErrorData(checkSlugRespError) || checkSlugRespData,
        t,
        t("workspace.channels.messages.error.checkSlug.description")
      ),
    })
  }, [
    applySlugIssue,
    checkSlugRespError,
    checkSlugRespData,
    alertToast,
    slugValue,
    t,
  ])

  // Generate slug from name
  const handleGenerateSlug = () => {
    const generatedSlug = nameValue
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "")
    form.setValue("slug", generatedSlug, { shouldValidate: true })
  }

  const handleSubmit = (data: ChannelFormData) => {
    if (isReservedClientChannelSlug(data.slug)) {
      setSlugCheckStatus("reserved")
      applySlugIssue("reserved")
      alertToast({
        title: t("workspace.channels.messages.error.createChannel.title"),
        description: t(
          "workspace.channels.messages.error.createChannel.slugReserved"
        ),
      })
      return
    }

    // Prevent submission if slug is unavailable
    if (slugCheckStatus === "unavailable") {
      alertToast({
        title: t("workspace.channels.messages.error.createChannel.title"),
        description: t(
          "workspace.channels.messages.error.createChannel.slugTaken"
        ),
      })
      return
    }

    if (slugCheckStatus === "reserved") {
      alertToast({
        title: t("workspace.channels.messages.error.createChannel.title"),
        description: t(
          "workspace.channels.messages.error.createChannel.slugReserved"
        ),
      })
      return
    }

    // Wait for slug check if still checking
    if (slugCheckStatus === "checking") {
      alertToast({
        title: t(
          "workspace.channels.messages.error.createChannel.checkingSlug.title"
        ),
        description: t(
          "workspace.channels.messages.error.createChannel.checkingSlug.description"
        ),
      })
      return
    }

    // Create initial config based on channel type
    let config: ClientChannelConfig
    switch (data.type) {
      case ClientChannelType.WEB:
        config = {
          type: "web",
          descriptions: [],
          title: data.name.trim(),
          allowAnonymousAccess: false,
          validRedeemCode: "",
          adButtons: [],
        } as WebClientConfig
        break
      case ClientChannelType.WIDGET:
        config = {
          type: "widget",
          agentId: prefillAgentId,
          allowAnonymousAccess: true,
          loginEnabled: false,
          loginVerificationMode: "assertion",
          originPolicyMode: "disabled",
          allowedOrigins: [],
          ui: {
            title: data.name.trim(),
            position: "bottom-right",
            locale: "en",
            launcher: { label: "Open chat" },
            panel: {
              greeting: "",
              suggestions: [],
            },
            theme: { baseColor: "default" },
          },
        } as WidgetClientConfig
        break
      case ClientChannelType.BROWSER_EXTENSION:
        config = {
          type: "browser_extension",
          extensionId: "",
        } as ExtensionClientConfig
        break
      case ClientChannelType.SLACK:
        // Slack channels require additional setup after creation
        // The config will be updated when the user configures the Slack connection
        // Note: agentId is intentionally omitted as it's optional for initial creation
        config = {
          type: "slack",
          botToken: "",
          signingSecret: "",
          teamId: "",
          teamName: "",
          agentId: prefillAgentId,
        } as SlackClientConfig
        break
      case ClientChannelType.LINE:
        config = {
          type: "line",
          channelSecret: "",
          channelAccessToken: "",
          liffId: "",
          lineLoginChannelId: "",
          agentId: prefillAgentId,
          requireEmailVerification: true,
          skipKeywords: [],
        } as LineClientConfig
        break
      case ClientChannelType.MESSENGER:
        config = {
          type: "messenger",
          pageId: "",
          webhookVerifyToken: "",
          metaAppSecret: "",
          accessToken: "",
          agentId: prefillAgentId,
        } as MessengerClientConfig
        break
      case ClientChannelType.INSTAGRAM:
        config = {
          type: "instagram",
          accountId: "",
          webhookVerifyToken: "",
          metaAppSecret: "",
          accessToken: "",
          agentId: prefillAgentId,
        } as InstagramClientConfig
        break
      default:
        config = {
          type: "web",
          descriptions: [],
          title: data.name.trim(),
          allowAnonymousAccess: false,
          validRedeemCode: "",
          adButtons: [],
        } as WebClientConfig
    }

    createChannel({
      jsonBody: makeCreateClientChannelRequest(
        data.name.trim(),
        data.slug.trim(),
        data.type,
        config
      ),
    })
  }

  // Handle create channel success
  useEffect(() => {
    if (!createChannelRespData || !createChannelRespData.data) return
    const newChannel = clientChannelResponseToClientChannel(
      createChannelRespData.data
    )
    onCreated?.(newChannel)
    successToast({
      title: t("workspace.channels.messages.created.title"),
      description: t("workspace.channels.messages.created.description"),
    })

    // Reset form and close dialog
    form.reset()
    setSlugCheckStatus("idle")
    onOpenChange?.(false)
  }, [createChannelRespData, onCreated, successToast, form, onOpenChange, t])

  // Handle create channel error
  useEffect(() => {
    if (!createChannelRespError) return

    const normalizedError = normalizeApiError(createChannelRespError)
    if (normalizedError.messageKey) {
      alertToast({
        title: t("workspace.channels.messages.error.createChannel.title"),
        description: t(normalizedError.messageKey),
      })
      return
    }

    const errorData = normalizedError.errorData
    const slugIssue = getClientChannelSlugIssue(
      slugValue,
      errorData?.message || createChannelRespData?.message
    )
    if (slugIssue) {
      setSlugCheckStatus(slugIssue === "reserved" ? "reserved" : "unavailable")
      applySlugIssue(slugIssue)
      alertToast({
        title: t("workspace.channels.messages.error.createChannel.title"),
        description: t(
          slugIssue === "reserved"
            ? "workspace.channels.messages.error.createChannel.slugReserved"
            : "workspace.channels.messages.error.createChannel.slugTaken"
        ),
      })
      return
    }

    const limitErrorData = errorData || createChannelRespData
    const limitKey = getLimitErrorTranslationKey(
      (limitErrorData as { error_code?: number } | null | undefined)?.error_code
    )
    const description = limitKey
      ? t(limitKey)
      : getUserFacingApiErrorMessage(
          createChannelRespError,
          t,
          createChannelRespData?.message ||
            t("workspace.channels.messages.error.createChannel.description")
        )
    alertToast({
      title: t("workspace.channels.messages.error.createChannel.title"),
      description,
    })
  }, [
    applySlugIssue,
    createChannelRespError,
    createChannelRespData,
    alertToast,
    slugValue,
    t,
  ])

  const handleCancel = () => {
    form.reset()
    setSlugCheckStatus("idle")
    onOpenChange?.(false)
  }

  // Render slug status indicator
  const renderSlugStatus = () => {
    if (!slugValue) return null

    switch (slugCheckStatus) {
      case "checking":
        return (
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>{t("workspace.channels.form.slugStatus.checking")}</span>
          </div>
        )
      case "available":
        return (
          <div className="flex items-center gap-1.5 text-sm text-green-600 dark:text-green-500">
            <CheckCircle2 className="h-4 w-4" />
            <span>{t("workspace.channels.form.slugStatus.available")}</span>
          </div>
        )
      case "unavailable":
        return (
          <div className="flex items-center gap-1.5 text-sm text-destructive">
            <XCircle className="h-4 w-4" />
            <span>{t("workspace.channels.form.slugStatus.unavailable")}</span>
          </div>
        )
      case "reserved":
        return (
          <div className="flex items-center gap-1.5 text-sm text-destructive">
            <XCircle className="h-4 w-4" />
            <span>{t("workspace.channels.form.slugStatus.reserved")}</span>
          </div>
        )
      default:
        return null
    }
  }

  const selectedType = form.watch("type")

  const isLoading =
    createChannelRespStatus === FetchStatus.Loading ||
    checkSlugRespStatus === FetchStatus.Loading

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t("workspace.channels.form.createTitle")}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            {/* Name */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("workspace.channels.form.nameLabel")}{" "}
                    <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("workspace.channels.form.namePlaceholder")}
                      autoFocus
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Slug */}
            <FormField
              control={form.control}
              name="slug"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center gap-2">
                    <FormLabel>
                      {t("workspace.channels.form.slugLabel")}{" "}
                      <span className="text-destructive">*</span>
                    </FormLabel>
                    <button
                      type="button"
                      onClick={handleGenerateSlug}
                      disabled={!nameValue.trim()}
                      className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline transition-colors"
                    >
                      {t("workspace.channels.form.autoFillSlug")}
                    </button>
                  </div>
                  <FormControl>
                    <Input
                      placeholder={t("workspace.channels.form.slugPlaceholder")}
                      className="font-mono text-sm"
                      {...field}
                      onChange={(e) => {
                        const value = e.target.value
                          .toLowerCase()
                          .replace(/\s+/g, "-")
                          .replace(/[^a-z0-9-]/g, "")
                        field.onChange(value)
                      }}
                    />
                  </FormControl>
                  {renderSlugStatus()}
                  <FormDescription>
                    {t("workspace.channels.form.slugDescription")}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Type */}
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("workspace.channels.form.typeLabel")}{" "}
                    <span className="text-destructive">*</span>
                  </FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={ClientChannelType.WEB}>
                        {t("workspace.channels.types.web")}
                      </SelectItem>
                      <SelectItem value={ClientChannelType.WIDGET}>
                        {t("workspace.channels.types.widget")}
                      </SelectItem>
                      <SelectItem value={ClientChannelType.SLACK}>
                        {t("workspace.channels.types.slack")}
                      </SelectItem>
                      <SelectItem value={ClientChannelType.LINE}>
                        {t("workspace.channels.types.line")}
                      </SelectItem>
                      <SelectItem value={ClientChannelType.MESSENGER}>
                        {t("workspace.channels.types.messenger")}
                      </SelectItem>
                      <SelectItem value={ClientChannelType.INSTAGRAM}>
                        {t("workspace.channels.types.instagram")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    {selectedType === ClientChannelType.WEB
                      ? t("workspace.channels.form.typeDescription.web")
                      : selectedType === ClientChannelType.WIDGET
                        ? t("workspace.channels.form.typeDescription.widget")
                        : selectedType === ClientChannelType.SLACK
                          ? t("workspace.channels.form.typeDescription.slack")
                          : selectedType === ClientChannelType.LINE
                            ? t("workspace.channels.form.typeDescription.line")
                            : selectedType === ClientChannelType.MESSENGER
                              ? t(
                                  "workspace.channels.form.typeDescription.messenger"
                                )
                              : selectedType === ClientChannelType.INSTAGRAM
                                ? t(
                                    "workspace.channels.form.typeDescription.instagram"
                                  )
                                : t(
                                    "workspace.channels.form.typeDescription.browserExtension"
                                  )}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isLoading}
              >
                {t("workspace.channels.form.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={
                  isLoading ||
                  slugCheckStatus === "checking" ||
                  slugCheckStatus === "unavailable" ||
                  slugCheckStatus === "reserved"
                }
              >
                {isLoading
                  ? t("workspace.channels.form.creating")
                  : t("workspace.channels.form.createButton")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

export default ChannelDialog
